/**
 * Authentication Library
 * Wraps Firebase Auth with clean API and error handling
 */

import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  updateProfile as firebaseUpdateProfile,
  onAuthStateChanged,
  User as FirebaseUser,
  UserCredential,
  GoogleAuthProvider,
  signInWithPopup,
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, isFirebaseConfigured } from './firebase';
import {
  UserProfile,
  AuthCredentials,
  RegisterData,
  UpdateProfileData,
  createDefaultProfile,
} from '@/types/user';

export type AuthErrorCode =
  | 'auth/email-already-in-use'
  | 'auth/invalid-email'
  | 'auth/operation-not-allowed'
  | 'auth/weak-password'
  | 'auth/user-disabled'
  | 'auth/user-not-found'
  | 'auth/wrong-password'
  | 'auth/too-many-requests'
  | 'auth/network-request-failed'
  | 'auth/popup-closed-by-user'
  | 'auth/not-configured'
  | 'unknown';

export interface AuthResult<T = void> {
  success: boolean;
  data?: T;
  error?: {
    code: AuthErrorCode;
    message: string;
  };
}

const ERROR_MESSAGES: Record<AuthErrorCode, string> = {
  'auth/email-already-in-use': 'An account with this email already exists.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/operation-not-allowed': 'This sign-in method is not enabled.',
  'auth/weak-password': 'Password should be at least 6 characters.',
  'auth/user-disabled': 'This account has been disabled.',
  'auth/user-not-found': 'No account found with this email.',
  'auth/wrong-password': 'Incorrect password. Please try again.',
  'auth/too-many-requests': 'Too many attempts. Please try again later.',
  'auth/network-request-failed': 'Network error. Please check your connection.',
  'auth/popup-closed-by-user': 'Sign-in popup was closed.',
  'auth/not-configured': 'Authentication is not configured.',
  'unknown': 'An unexpected error occurred. Please try again.',
};

function getErrorMessage(code: string): { code: AuthErrorCode; message: string } {
  const errorCode = code as AuthErrorCode;
  return {
    code: ERROR_MESSAGES[errorCode] ? errorCode : 'unknown',
    message: ERROR_MESSAGES[errorCode] || ERROR_MESSAGES['unknown'],
  };
}

function checkConfiguration(): AuthResult {
  if (!isFirebaseConfigured() || !auth || !db) {
    return {
      success: false,
      error: getErrorMessage('auth/not-configured'),
    };
  }
  return { success: true };
}

async function createUserProfile(user: FirebaseUser): Promise<UserProfile> {
  if (!db) throw new Error('Firestore not configured');

  const profile = createDefaultProfile(user.uid, user.email || '', user.displayName);
  const userRef = doc(db, 'users', user.uid);

  await setDoc(userRef, {
    ...profile,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return profile;
}

async function getUserProfile(uid: string): Promise<UserProfile | null> {
  if (!db) return null;

  const userRef = doc(db, 'users', uid);
  const snapshot = await getDoc(userRef);

  if (!snapshot.exists()) {
    return null;
  }

  const data = snapshot.data();
  return {
    ...data,
    id: snapshot.id,
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date(),
  } as UserProfile;
}

export async function register(data: RegisterData): Promise<AuthResult<UserProfile>> {
  const configCheck = checkConfiguration();
  if (!configCheck.success) return configCheck as AuthResult<UserProfile>;

  try {
    const credential: UserCredential = await createUserWithEmailAndPassword(
      auth!,
      data.email,
      data.password
    );

    if (data.displayName) {
      await firebaseUpdateProfile(credential.user, {
        displayName: data.displayName,
      });
    }

    const profile = await createUserProfile(credential.user);

    return { success: true, data: profile };
  } catch (error: unknown) {
    const firebaseError = error as { code?: string };
    return {
      success: false,
      error: getErrorMessage(firebaseError.code || 'unknown'),
    };
  }
}

export async function login(credentials: AuthCredentials): Promise<AuthResult<UserProfile>> {
  const configCheck = checkConfiguration();
  if (!configCheck.success) return configCheck as AuthResult<UserProfile>;

  try {
    const credential = await signInWithEmailAndPassword(
      auth!,
      credentials.email,
      credentials.password
    );

    let profile = await getUserProfile(credential.user.uid);

    if (!profile) {
      profile = await createUserProfile(credential.user);
    }

    return { success: true, data: profile };
  } catch (error: unknown) {
    const firebaseError = error as { code?: string };
    return {
      success: false,
      error: getErrorMessage(firebaseError.code || 'unknown'),
    };
  }
}

export async function loginWithGoogle(): Promise<AuthResult<UserProfile>> {
  const configCheck = checkConfiguration();
  if (!configCheck.success) return configCheck as AuthResult<UserProfile>;

  try {
    const provider = new GoogleAuthProvider();
    const credential = await signInWithPopup(auth!, provider);

    let profile = await getUserProfile(credential.user.uid);

    if (!profile) {
      profile = await createUserProfile(credential.user);
    }

    return { success: true, data: profile };
  } catch (error: unknown) {
    const firebaseError = error as { code?: string };
    return {
      success: false,
      error: getErrorMessage(firebaseError.code || 'unknown'),
    };
  }
}

export async function logout(): Promise<AuthResult> {
  const configCheck = checkConfiguration();
  if (!configCheck.success) return configCheck;

  try {
    await firebaseSignOut(auth!);
    return { success: true };
  } catch (error: unknown) {
    const firebaseError = error as { code?: string };
    return {
      success: false,
      error: getErrorMessage(firebaseError.code || 'unknown'),
    };
  }
}

export async function resetPassword(email: string): Promise<AuthResult> {
  const configCheck = checkConfiguration();
  if (!configCheck.success) return configCheck;

  try {
    await sendPasswordResetEmail(auth!, email);
    return { success: true };
  } catch (error: unknown) {
    const firebaseError = error as { code?: string };
    return {
      success: false,
      error: getErrorMessage(firebaseError.code || 'unknown'),
    };
  }
}

export async function updateProfile(
  uid: string,
  data: UpdateProfileData
): Promise<AuthResult<UserProfile>> {
  const configCheck = checkConfiguration();
  if (!configCheck.success) return configCheck as AuthResult<UserProfile>;

  try {
    const userRef = doc(db!, 'users', uid);
    await updateDoc(userRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });

    if (auth!.currentUser && (data.displayName || data.photoURL)) {
      await firebaseUpdateProfile(auth!.currentUser, {
        displayName: data.displayName,
        photoURL: data.photoURL,
      });
    }

    const updatedProfile = await getUserProfile(uid);
    if (!updatedProfile) {
      return {
        success: false,
        error: getErrorMessage('auth/user-not-found'),
      };
    }

    return { success: true, data: updatedProfile };
  } catch (error: unknown) {
    const firebaseError = error as { code?: string };
    return {
      success: false,
      error: getErrorMessage(firebaseError.code || 'unknown'),
    };
  }
}

export function subscribeToAuthChanges(
  callback: (user: FirebaseUser | null) => void
): () => void {
  if (!auth) {
    callback(null);
    return () => {};
  }

  return onAuthStateChanged(auth, callback);
}

export { getUserProfile };
